import { useState, useEffect } from "react"
import Modal from "aldoo-ra/Modal"
import { HistoryAPI } from "aldoo-ra/Admin/History/history-api"
import { api } from "aldoo-ra/API"
import { url } from "aldoo-ra/CMS/url"
import { formatDistanceToNow, format } from "date-fns"
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline"
import Tooltip from "aldoo-ra/Tooltip"

const crudAPI = api("crud")

export default function ContentHistoryDialog({ contentId, contentType, isOpen, onClose }) {
  const [contributors, setContributors] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")

  useEffect(() => {
    if (isOpen && contentId) {
      loadHistory()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, contentId, contentType])

  const loadHistory = async () => {
    setLoading(true)
    try {
      // Prefix with "CMS." if not already present and it's a CMS content type
      const targetModel = contentType?.startsWith('CMS.')
        ? contentType
        : contentType
          ? `CMS.${contentType}`
          : 'CMSContent'

      // Get history for this content
      const historyResponse = await HistoryAPI.GetTargetHistory(contentId, targetModel)

      if (historyResponse.result && historyResponse.data) {
        // Extract unique users from history entries
        const userMap = new Map()

        historyResponse.data.forEach(entry => {
          // Handle both populated user objects and user IDs
          const userId = typeof entry.user === 'object' ? entry.user._id : entry.user
          const userName = typeof entry.user === 'object' ? entry.user.name : entry.author
          const userEmail = typeof entry.user === 'object' ? entry.user.email : null
          const userAvatar = typeof entry.user === 'object' ? entry.user.avatar : null

          if (userId && !userMap.has(userId)) {
            userMap.set(userId, {
              _id: userId,
              name: userName,
              email: userEmail,
              avatar: userAvatar,
              lastChange: entry.timestamp,
              changeCount: 1
            })
          } else if (userId) {
            const existing = userMap.get(userId)
            existing.changeCount++
            // Keep the most recent timestamp
            if (entry.timestamp > existing.lastChange) {
              existing.lastChange = entry.timestamp
            }
          }
        })

        // Convert map to array and sort by most recent change (newest first)
        const contributorsData = Array.from(userMap.values())
        contributorsData.sort((a, b) => new Date(b.lastChange) - new Date(a.lastChange))
        setContributors(contributorsData)
      }
    } catch (error) {
      console.error('Failed to load content history:', error)
    } finally {
      setLoading(false)
    }
  }

  const filteredContributors = contributors.filter(contributor => {
    if (!searchTerm) return true
    const search = searchTerm.toLowerCase()
    return (
      contributor.name?.toLowerCase().includes(search) ||
      contributor.email?.toLowerCase().includes(search)
    )
  })

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Changes"
      variant="admin_compact"
      styleOverrides={{
        container: "bg-admin_paper max-h-full md:max-h-[calc(80vh)] scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper overflow-y-auto md:rounded-md border border-admin_bordered shadow-lg w-full max-w-[400px] p-0 m-0"
      }}
    >
      {/* Search Bar */}
      <div className="relative mb-3 px-3">
        <MagnifyingGlassIcon className="absolute left-6 top-1/2 transform -translate-y-1/2 w-4 h-4 text-admin_text_secondary" />
        <input
          type="text"
          placeholder="Search contributors..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-9 pr-3 py-2 text-sm bg-admin_canvas border border-admin_bordered rounded-md text-admin_text placeholder-admin_text_secondary focus:outline-none focus:border-admin_primary"
        />
      </div>

      {/* Contributors List */}
      <div className="px-3 max-h-[400px] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-sm text-admin_text_secondary">Loading history...</p>
          </div>
        ) : filteredContributors.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-sm text-admin_text_secondary">
              {searchTerm ? 'No contributors found' : 'No changes recorded yet'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-admin_bordered">
            {filteredContributors.map(contributor => (
              <div
                key={contributor._id}
                className="flex items-center gap-2 px-2 py-1.5 hover:bg-admin_canvas transition-colors"
              >
                {/* Avatar */}
                <div className="flex-shrink-0">
                  {contributor.avatar ? (
                    <img
                      src={url(contributor.avatar)}
                      alt={contributor.name || contributor.email}
                      className="w-8 h-8 rounded-full object-cover bg-white"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-admin_primary flex items-center justify-center">
                      <span className="text-white font-medium text-sm">
                        {(contributor.name || contributor.email || 'U').charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>

                {/* User Info - Name and email */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm text-admin_text font-medium truncate leading-none mb-px">
                      {contributor.name || 'Unknown User'}
                    </p>
                    {contributor.changeCount > 1 && (
                      <span className="text-xs text-admin_text_secondary flex-shrink-0">
                        ({contributor.changeCount})
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-admin_text_secondary truncate leading-none">
                    {contributor.email}
                  </p>
                </div>

                {/* Last Change Time with Tooltip */}
                <Tooltip
                  content={format(new Date(contributor.lastChange), 'PPpp')}
                  position="left"
                >
                  <div className="flex-shrink-0 text-right cursor-help">
                    <p className="text-xs text-admin_text_secondary whitespace-nowrap">
                      {formatDistanceToNow(new Date(contributor.lastChange), { addSuffix: true })}
                    </p>
                  </div>
                </Tooltip>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  )
}
